import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { statusBadge, ASSET_ICONS, ASSET_LABELS, formatDate, formatDateTime, isITRole } from '../utils';
import TransferModal from '../components/TransferModal';
import DamageModal from '../components/DamageModal';
import EditAssetModal from '../components/EditAssetModal';
import RetireAssetModal from '../components/RetireAssetModal';

export default function AssetDetail() {
  const { id } = useParams();
  const { withAuth, user } = useAuth();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [damageOpen, setDamageOpen] = useState(false);
  const [mdmData, setMdmData] = useState(null);
  const [mdmLoading, setMdmLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);

  // Three separate gates, because the endpoints behind them differ.
  //
  // Editing fields (rename, notes, Edit Details) goes through PATCH
  // /assets/:id, which authorizes it_admin and it_staff only. A single canEdit
  // flag including supervisor showed them a rename button that the server
  // answered with 403.
  //
  // Transfer and Report Issue post to /transfers and /damage, which carry no
  // authorize() call at all, so supervisor keeps the access it already had.
  //
  // Retiring is DELETE /assets/:id — it_admin alone, the only single-role
  // endpoint in the API.
  const canEditFields = isITRole(user?.role);
  const canTransfer = ['it_admin', 'it_staff', 'supervisor'].includes(user?.role);
  const canRetire = user?.role === 'it_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const res = await api.getAsset(id);
        setAsset(res);
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id, withAuth]);

  useEffect(() => { load(); }, [load]);

  const assetType = asset?.asset_type;
  useEffect(() => {
    if (!assetType) return;
    if (!['cell_phone', 'tablet'].includes(assetType)) return;
    setMdmLoading(true);
    withAuth(async () => {
      try {
        const data = await api.getHexnodeDetail(id);
        setMdmData(data);
      } catch (err) {
        setMdmData(null);
      } finally {
        setMdmLoading(false);
      }
    });
  }, [assetType, id, withAuth]);

  if (loading) return <div className="empty-state" style={{ height: '100%' }}><div className="spinner" /></div>;
  if (!asset) return <div className="empty-state">Asset not found</div>;

  const sb = statusBadge(asset.status);

  async function handleRename() {
    if (!newName.trim() || newName.trim() === asset.display_name) {
      setRenaming(false);
      return;
    }
    setRenameSaving(true);
    setRenameError('');
    try {
      // Update name in AssetTrack
      await withAuth(() => api.updateAsset(asset.id, { displayName: newName.trim() }));
      // If mobile asset with hexnode_device_id, update Hexnode too
      if (asset.details?.hexnode_device_id) {
        try {
          await api.post('/assets/rename-hexnode', {
            hexnode_device_id: asset.details.hexnode_device_id,
            display_name: newName.trim()
          });
        } catch (hexErr) {
          console.warn('Hexnode rename failed:', hexErr.message);
        }
      }
      setAsset(prev => ({ ...prev, display_name: newName.trim() }));
      setRenaming(false);
    } catch (err) {
      // Stay in the editor holding the typed name — closing it discarded the
      // edit and left the old name on screen with no indication of failure.
      setRenameError(err.message || 'Rename failed');
    } finally {
      setRenameSaving(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-sm" onClick={() => navigate('/assets')}>← Back</button>
        <span style={{ fontSize: 24 }}>{ASSET_ICONS[asset.asset_type]}</span>
        <div style={{ flex: 1 }}>
          {renaming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
                style={{ fontSize: 16, fontWeight: 600, width: 320, padding: '4px 8px' }}
              />
              <button className="btn btn-primary btn-sm" onClick={handleRename} disabled={renameSaving}>
                {renameSaving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-sm" onClick={() => { setRenaming(false); setRenameError(''); }}>Cancel</button>
              {renameError && <span style={{ color: 'var(--red)', fontSize: 13 }}>{renameError}</span>}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h1 style={{ fontSize: 18, fontWeight: 600 }}>{asset.display_name}</h1>
              {canEditFields && (
                <button
                  onClick={() => { setNewName(asset.display_name); setRenaming(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}
                  title="Rename asset"
                >✎</button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
            <span className="mono">{asset.asset_tag}</span>
            <span style={{ color: 'var(--text-2)' }}>·</span>
            <span className="chip">{ASSET_LABELS[asset.asset_type]}</span>
            <span className={`badge ${sb.className}`}>{sb.label}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {canEditFields && (
            <button className="btn" onClick={() => setEditOpen(true)}>✎ Edit Details</button>
          )}
          {canTransfer && (
            <>
              <button className="btn" onClick={() => setTransferOpen(true)}>⇄ Transfer</button>
              <button className="btn" onClick={() => setDamageOpen(true)}>⚠ Report Issue</button>
            </>
          )}
          {canRetire && (
            <button
              className="btn btn-danger"
              onClick={() => setRetireOpen(true)}
              title="Retire this asset (IT admin only)"
              style={{ marginLeft: 4 }}
            >
              🗑 Retire
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: 28, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Device Info */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header"><h3>Device Details</h3></div>
          <div style={{ padding: '16px 20px' }}>
            {[
              { label: 'Make', value: asset.make },
              { label: 'Model', value: asset.model },
              { label: 'Serial Number', value: asset.serial_number, mono: true },
              { label: 'Acquired', value: formatDate(asset.acquired_date) },
              { label: 'Warranty Expires', value: formatDate(asset.warranty_expiry) },
            ].filter(r => r.value).map(({ label, value, mono }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                <span style={{ color: 'var(--text-2)' }}>{label}</span>
                <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 13 } : { fontWeight: 500 }}>{value}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 14, color: 'var(--text-2)' }}>Notes</span>
                {canEditFields && !notesEditing && (
                  <button onClick={() => { setNotesText(asset.notes || ''); setNotesEditing(true); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontSize: 13, padding: '2px 4px' }}>✎ Edit</button>
                )}
              </div>
              {notesEditing ? (
                <div>
                  <textarea value={notesText} onChange={e => setNotesText(e.target.value)} autoFocus
                    placeholder="Add notes..." rows={3}
                    style={{ width: '100%', padding: '6px 8px', background: 'var(--bg-2)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-0)', fontFamily: 'var(--font)', fontSize: 13, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button className="btn btn-primary btn-sm" disabled={notesSaving}
                      onClick={async () => {
                        setNotesSaving(true);
                        setNotesError('');
                        try {
                          await withAuth(() => api.updateAsset(asset.id, { notes: notesText.trim() }));
                          setAsset(prev => ({ ...prev, notes: notesText.trim() }));
                          setNotesEditing(false);
                        } catch (err) {
                          setNotesError(err.message || 'Could not save notes');
                        } finally {
                          setNotesSaving(false);
                        }
                      }}>
                      {notesSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-sm" onClick={() => { setNotesEditing(false); setNotesError(''); }}>Cancel</button>
                  </div>
                  {notesError && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 6 }}>{notesError}</div>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: asset.notes ? 'var(--text-0)' : 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
                  {asset.notes || (canEditFields ? 'No notes. Click Edit to add.' : '—')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Assignment */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-header"><h3>Assignment</h3></div>
          <div style={{ padding: '16px 20px' }}>
            {asset.employee_name ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}
                onClick={() => asset.assigned_to_employee_id && navigate(`/employees/${asset.assigned_to_employee_id}`)}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'var(--accent-dim)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 600, color: 'var(--accent-text)',
                }}>
                  {asset.employee_name.split(' ').map(w => w[0]).join('').slice(0,2)}
                </div>
                <div>
                  <div style={{ fontWeight: 500 }}>{asset.employee_name}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{asset.job_title}</div>
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>Not assigned to an employee</div>
            )}
            {[
              { label: 'Location', value: asset.location_name },
              { label: 'Workday ID', value: asset.workday_id, mono: true },
              { label: 'Hierarchy', value: asset.hierarchy_group },
            ].filter(r => r.value).map(({ label, value, mono }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                <span style={{ color: 'var(--text-2)' }}>{label}</span>
                <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 13 } : { fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Live MDM Data */}
        {(mdmLoading || mdmData) && (
          <div className="card" style={{ gridColumn: '1 / -1', padding: 0 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Live MDM Data <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 400 }}>via Hexnode</span></h3>
              {mdmData && <span className={`badge ${mdmData.compliant ? 'badge-green' : 'badge-red'}`}>{mdmData.compliant ? 'Compliant' : 'Non-Compliant'}</span>}
            </div>
            {mdmLoading
              ? <div className="empty-state"><div className="spinner" /></div>
              : mdmData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
                    {[
                      { label: 'IMEI', value: mdmData.imei, mono: true },
                      { label: 'IMEI 2', value: mdmData.imei2, mono: true },
                      { label: 'Phone Number', value: mdmData.phone_number },
                      { label: 'Manufacturer', value: mdmData.manufacturer },
                      { label: 'Storage', value: mdmData.device_capacity_gb ? `${mdmData.device_capacity_gb} GB total / ${mdmData.available_capacity_gb} GB free` : null },
                      { label: 'Battery', value: mdmData.battery_level != null ? `${mdmData.battery_level}%` : null },
                      { label: 'Enrollment', value: mdmData.enrollment_status },
                      { label: 'Enrolled', value: mdmData.enrolled_time ? new Date(mdmData.enrolled_time).toLocaleDateString() : null },
                      { label: 'Last Seen', value: mdmData.last_reported ? new Date(mdmData.last_reported).toLocaleString() : null },
                    ].filter(r => r.value).map(({ label, value, mono }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                        <span style={{ color: 'var(--text-2)' }}>{label}</span>
                        <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 13 } : { fontWeight: 500 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Last Known Location</div>
                    {mdmData.last_location
                      ? (
                        <>
                          <div style={{ fontSize: 14, marginBottom: 8 }}>{mdmData.last_location}</div>
                          {mdmData.last_location_time && (
                            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                              {new Date(mdmData.last_location_time).toLocaleString()}
                            </div>
                          )}
                          {mdmData.latitude && mdmData.longitude && (
                            <a
                              href={`https://www.google.com/maps?q=${mdmData.latitude},${mdmData.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: 13, color: 'var(--accent-text)', display: 'block', marginTop: 8 }}
                            >
                              View on Google Maps →
                            </a>
                          )}
                        </>
                      )
                      : <div style={{ color: 'var(--text-2)', fontSize: 14 }}>No location data available</div>
                    }
                  </div>
                </div>
              )
            }
            {/* Policies */}
            {mdmData && mdmData.policies && mdmData.policies.length > 0 && (
              <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Applied Policies</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {mdmData.policies.map(p => (
                    <span key={p.id} style={{
                      fontSize: 13, padding: '3px 10px',
                      background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)', color: 'var(--text-1)'
                    }}>
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Computer Details - for laptops, desktops, servers */}
        {['laptop', 'desktop', 'server'].includes(asset.asset_type) && asset.details && (
          <div className="card" style={{ gridColumn: '1 / -1', padding: 0 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Computer Details <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 400 }}>via Ninja RMM</span></h3>
              {asset.details.last_seen_at && (
                <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  Last seen: {new Date(asset.details.last_seen_at).toLocaleString()}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <div style={{ padding: '16px 20px', borderRight: '1px solid var(--border)' }}>
                {[
                  { label: 'Operating System', value: asset.details.os_version },
                  { label: 'CPU', value: asset.details.cpu },
                  { label: 'RAM', value: asset.details.ram_gb ? asset.details.ram_gb + ' GB' : null },
                  { label: 'Storage', value: asset.details.storage_gb ? asset.details.storage_gb + ' GB' : null },
                  { label: 'Ninja Device ID', value: asset.details.ninja_device_id, mono: true },
                ].filter(r => r.value).map(({ label, value, mono }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                    <span style={{ color: 'var(--text-2)' }}>{label}</span>
                    <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 13 } : { fontWeight: 500, maxWidth: 300, textAlign: 'right' }}>{value}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '16px 20px' }}>
                {[
                  { label: 'Warranty Expires', value: asset.warranty_expiry ? new Date(asset.warranty_expiry).toLocaleDateString() : null },
                  { label: 'IP Address', value: asset.details.ip_address },
                  { label: 'MAC Address', value: asset.details.mac_address, mono: true },
                  { label: 'Domain', value: asset.details.domain },
                  { label: 'Asset Tag', value: asset.asset_tag, mono: true },
                ].filter(r => r.value).map(({ label, value, mono }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                    <span style={{ color: 'var(--text-2)' }}>{label}</span>
                    <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 13 } : { fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Transfer History */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header"><h3>Transfer History</h3></div>
          {!asset.transfers?.length
            ? <div className="empty-state">No transfers recorded</div>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Location</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {asset.transfers.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatDateTime(t.created_at)}</td>
                      <td style={{ fontSize: 14 }}>{t.from_employee || <span style={{ color: 'var(--text-2)' }}>Unassigned</span>}</td>
                      <td style={{ fontSize: 14 }}>{t.to_employee || <span style={{ color: 'var(--text-2)' }}>Unassigned</span>}</td>
                      <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{t.to_location || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{t.initiated_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>

        {/* Damage History */}
        {asset.damage?.length > 0 && (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header"><h3>Damage / Issue History</h3></div>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>HALO Ticket</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {asset.damage.map(d => (
                  <tr key={d.id}>
                    <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatDateTime(d.created_at)}</td>
                    <td><span className="chip">{d.damage_type?.replace(/_/g, ' ')}</span></td>
                    <td style={{ fontSize: 14, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.description}</td>
                    <td>
                      {d.halo_ticket_id
                        ? <span className="mono">#{d.halo_ticket_id}</span>
                        : <span style={{ color: 'var(--text-2)', fontSize: 13 }}>—</span>
                      }
                    </td>
                    <td><span className={`badge ${d.damage_status === 'reported' ? 'badge-yellow' : d.damage_status === 'repaired' ? 'badge-green' : 'badge-gray'}`}>{d.damage_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {transferOpen && <TransferModal asset={asset} onClose={() => setTransferOpen(false)} onSuccess={() => { setTransferOpen(false); load(); }} />}
      {damageOpen && <DamageModal asset={asset} onClose={() => setDamageOpen(false)} onSuccess={() => { setDamageOpen(false); load(); }} />}
      {editOpen && (
        <EditAssetModal
          asset={asset}
          onClose={() => setEditOpen(false)}
          onSuccess={() => { setEditOpen(false); load(); }}
        />
      )}
      {retireOpen && (
        <RetireAssetModal
          asset={asset}
          onClose={() => setRetireOpen(false)}
          // The asset is soft-deleted, so this page would 404 on reload —
          // return to the list rather than re-fetching a row that's now hidden.
          onSuccess={() => { setRetireOpen(false); navigate('/assets'); }}
        />
      )}
    </div>
  );
}
